import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, setDoc, query, where, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { CalendarCheck, Save } from "lucide-react";
import { toast } from "sonner";

const AdminAttendance = () => {
  const [students, setStudents] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [topic, setTopic] = useState("");
  const [statuses, setStatuses] = useState({}); // studentId -> "present" | "absent"
  const [saving, setSaving] = useState(false);
  const [courseFilter, setCourseFilter] = useState("all");

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, "students"));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) =>
          ["paid", "free_via_coupon", "success"].includes(s.paymentStatus) || Number(s.amountPaid) === 0
        );
        setStudents(list);
      } catch (error) {
        toast.error(error.message || "Unable to load students");
      }
    };
    fetch();
  }, []);

  // Load existing attendance for the chosen date
  useEffect(() => {
    const fetch = async () => {
      const q = query(collection(db, "attendance"), where("date", "==", date));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        map[data.studentId] = data.status;
      });
      setStatuses(map);
    };
    fetch();
  }, [date]);

  const set = (studentId, status) => setStatuses((p) => ({ ...p, [studentId]: status }));

  const filteredStudents = students.filter((s) => courseFilter === "all" || s.selectedProgramId === courseFilter);
  const uniqueCourses = [...new Set(students.map((s) => s.selectedProgramId).filter(Boolean))];

  const markAllPresent = () => {
    const m = { ...statuses };
    filteredStudents.forEach((s) => { m[s.studentId] = "present"; });
    setStatuses(m);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const s of filteredStudents) {
        const status = statuses[s.studentId];
        if (!status) continue;
        const attId = `${s.studentId}_${date}`;
        await setDoc(doc(db, "attendance", attId), {
          attendanceId: attId,
          uid: s.uid || null,
          studentId: s.studentId,
          courseId: s.selectedProgramId,
          date,
          topic,
          status,
          markedAt: serverTimestamp(),
        });
      }
      // Recompute attendance % per student
      for (const s of filteredStudents) {
        const q = query(collection(db, "attendance"), where("studentId", "==", s.studentId));
        const snap = await getDocs(q);
        const total = snap.size;
        const present = snap.docs.filter((d) => d.data().status === "present").length;
        const pct = total > 0 ? Math.round((present / total) * 100) : 0;
        await updateDoc(doc(db, "students", s.studentId), { attendancePercentage: pct });
      }
      toast.success("Attendance saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-attendance">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Attendance Management</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Mark Attendance</h1>
        <p className="mt-1 text-slate-600">Choose a date, optionally filter by course, then mark each student.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-slate-600 font-medium mb-1">Date</label>
            <input type="date" data-testid="attendance-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label className="block text-xs text-slate-600 font-medium mb-1">Course Filter</label>
            <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} data-testid="attendance-course-filter" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
              <option value="all">All Courses</option>
              {uniqueCourses.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-600 font-medium mb-1">Class Topic (optional)</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. HTML Basics" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={markAllPresent} className="px-4 py-2 rounded-lg border border-slate-200 text-sm hover:bg-slate-50">Mark all present</button>
          <button onClick={saveAll} disabled={saving} data-testid="save-attendance-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Attendance"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {filteredStudents.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-students-attendance">No students available yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Student ID</th>
                <th className="text-left px-6 py-3 font-semibold">Name</th>
                <th className="text-left px-6 py-3 font-semibold">Course</th>
                <th className="text-left px-6 py-3 font-semibold">Status for {date}</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s) => {
                const status = statuses[s.studentId];
                return (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-6 py-3 font-mono text-xs">{s.studentId}</td>
                    <td className="px-6 py-3 font-medium text-slate-900">{s.fullName}</td>
                    <td className="px-6 py-3 text-slate-700">{s.selectedInternship}</td>
                    <td className="px-6 py-3">
                      <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                        <button
                          onClick={() => set(s.studentId, "present")}
                          data-testid={`present-btn-${s.studentId}`}
                          className={`px-3 py-1.5 text-xs font-semibold ${status === "present" ? "bg-green-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                        >Present</button>
                        <button
                          onClick={() => set(s.studentId, "absent")}
                          data-testid={`absent-btn-${s.studentId}`}
                          className={`px-3 py-1.5 text-xs font-semibold border-l border-slate-200 ${status === "absent" ? "bg-red-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                        >Absent</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminAttendance;
